import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Calculator, AlertTriangle, RefreshCw, Tag, GripVertical, ExternalLink, Printer } from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router-dom";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function ManufacturingCost() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() === 0 ? 12 : today.getMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Pricebooks (filtered by Integrations visibility settings)
  const [pricebooks, setPricebooks] = useState([]);
  const [selectedPbId, setSelectedPbId] = useState(null);
  const [pbItemPrices, setPbItemPrices] = useState({});  // {item_id: {rate, rand_per_kg, source}}
  const [pbBusy, setPbBusy] = useState(false);
  const [pbSyncedAt, setPbSyncedAt] = useState(null);

  // Local row order (snapshot of API data; persisted via /sales-items/reorder on drop)
  const [orderedRows, setOrderedRows] = useState([]);

  const load = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/manufacturing-cost?year=${year}&month=${month}`);
      setData(data);
      setOrderedRows(data.rows || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  // Initial pricebooks load (visibility-filtered)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/zoho/pricebooks");
        setPricebooks(data.pricebooks || []);
        setPbSyncedAt(data.synced_at);
        const def = (data.pricebooks || []).find((p) => p.is_default) || (data.pricebooks || [])[0];
        if (def && !selectedPbId) setSelectedPbId(def.id);
      } catch { /* non-blocking */ }
    })();
    // eslint-disable-next-line
  }, []);

  // Per-item price lookup whenever pricebook changes
  useEffect(() => {
    if (!selectedPbId) return;
    (async () => {
      setPbBusy(true);
      try {
        const { data } = await api.get(`/zoho/pricebooks/${selectedPbId}/item-prices`);
        setPbItemPrices(data.items || {});
      } catch { setPbItemPrices({}); }
      finally { setPbBusy(false); }
    })();
  }, [selectedPbId]);

  const refreshPricebooks = async () => {
    setPbBusy(true);
    try {
      const { data } = await api.get("/zoho/pricebooks?refresh=true");
      setPricebooks(data.pricebooks || []);
      setPbSyncedAt(data.synced_at);
      if (selectedPbId) {
        const r = await api.get(`/zoho/pricebooks/${selectedPbId}/item-prices`);
        setPbItemPrices(r.data.items || {});
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPbBusy(false); }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = orderedRows.findIndex((r) => r.item_id === active.id);
    const newIdx = orderedRows.findIndex((r) => r.item_id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(orderedRows, oldIdx, newIdx);
    setOrderedRows(next);
    // Persist order
    try {
      await api.post("/sales-items/reorder", { item_ids: next.map((r) => r.item_id) });
    } catch { /* non-blocking; will reorder on next load */ }
  };

  const yearChoices = useMemo(() => {
    const y = today.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, [today]);

  const t = data?.totals;

  return (
    <div data-testid="manufacturing-cost-page" className="mfg-cost-print-root">
      {/* Print-only stylesheet — keeps screen layout untouched */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 10mm 10mm 12mm 10mm; }
          html, body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* Hide chrome (sidebar/topbar/etc) — anything outside our root */
          body > *:not(.preview-screenshot) { visibility: hidden; }
          .mfg-cost-print-root, .mfg-cost-print-root * { visibility: visible; }
          .mfg-cost-print-root {
            position: absolute; left: 0; top: 0; width: 100%;
            color: #1a1a1a; font-size: 9pt;
          }
          /* Hide UI controls that don't belong in a printed report */
          .no-print { display: none !important; }
          /* Ensure the print-header/footer (display:none on screen) actually show */
          .print-only { display: block !important; }
          .print-only-flex { display: flex !important; }
          /* Prevent row splits */
          tr, .keep-together { page-break-inside: avoid; break-inside: avoid; }
          thead { display: table-header-group; }
          /* Table tightening */
          table { font-size: 8.5pt; border-collapse: collapse; width: 100%; }
          th, td { padding: 4px 6px !important; }
          .bg-card, .bg-secondary, .bg-secondary\\/40, .bg-secondary\\/30, .bg-primary\\/5, .bg-primary\\/10 {
            background: #fafaf7 !important;
          }
          /* Drag handles + action buttons disappear */
          [data-testid^="mfg-handle-"] { display: none !important; }
          .border, .border-border { border-color: #d4d4d0 !important; }
          .rounded { border-radius: 0 !important; }
          /* Force any sticky/fixed off so they don't overlap */
          .sticky, .fixed { position: static !important; }
          /* Hide the empty drag-handle column header */
          th:first-child { width: 0 !important; padding: 0 !important; }
        }
        /* Screen: hide print-only blocks */
        .print-only, .print-only-flex { display: none; }
      `}</style>

      <PageHeader
        eyebrow="Costing"
        title="Manufacturing Cost"
        description="Per-item cost, sell price, and gross margin for the chosen month. Items are opted in via Item Mapping. Drag the handle to reorder. Switch the price list to compare margins across distribution channels."
        actions={
          <button onClick={() => window.print()} data-testid="mfg-print-btn"
            className="no-print inline-flex items-center gap-2 px-4 py-2 border border-border bg-card rounded text-sm font-semibold hover:bg-secondary">
            <Printer className="w-4 h-4" /> Print to PDF
          </button>
        }
      />

      {/* PRINT-ONLY page header — only renders on the printed page */}
      <div className="print-only" style={{ marginBottom: "10mm" }}>
        <div className="print-only-flex" style={{ alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid #6b3c2e", paddingBottom: "6px" }}>
          <div>
            <p style={{ fontSize: "8pt", color: "#888", letterSpacing: "0.1em", textTransform: "uppercase", margin: 0 }}>Brothers Coffee Roastery · Costing</p>
            <h1 style={{ fontSize: "18pt", margin: "2px 0 0 0", fontWeight: 700, color: "#6b3c2e" }}>
              Manufacturing Cost — {MONTHS[month - 1]} {year}
            </h1>
            <p style={{ fontSize: "8pt", color: "#666", margin: "1px 0 0 0" }}>
              Sell prices from {pricebooks.find((p) => p.id === selectedPbId)?.display_name || pricebooks.find((p) => p.id === selectedPbId)?.name || "default item rates"}
              {" · "} {orderedRows.length} item{orderedRows.length === 1 ? "" : "s"} included
            </p>
          </div>
          <div style={{ textAlign: "right", fontSize: "8pt", color: "#666" }}>
            <p style={{ margin: 0 }}>Generated {new Date().toLocaleString("en-ZA")}</p>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded p-4 mb-4 no-print">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <p className="label-tiny mb-1">Year</p>
            <select value={year} onChange={(e) => setYear(+e.target.value)}
              data-testid="mfg-year"
              className="px-3 py-2 border border-border rounded font-mono">
              {yearChoices.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <p className="label-tiny mb-1">Month</p>
            <select value={month} onChange={(e) => setMonth(+e.target.value)}
              data-testid="mfg-month"
              className="px-3 py-2 border border-border rounded font-mono">
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4" data-testid="mfg-error">{error}</div>}

      {data?.warnings?.length > 0 && (
        <div className="bg-[hsl(31,65%,53%)]/10 border border-[hsl(31,65%,53%)]/30 rounded p-3 mb-4 text-xs space-y-1 no-print" data-testid="mfg-warnings">
          {data.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-[hsl(31,65%,53%)] mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Cost buckets */}
      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="mfg-cost-buckets">
          <Bucket label="Gas" total={t.gas_total} perKg={t.gas_per_kg} kgSold={t.kg_sold} />
          <Bucket label="Freight" total={t.freight_total} perKg={t.freight_per_kg} kgSold={t.kg_sold} />
          <Bucket label="Labour" total={t.labour_total} perKg={t.labour_per_kg} kgSold={t.kg_sold}
                  hint={t.labour_total > 0 ? "from /equipment" : "set via Manufacturing Labour & Equipment"} />
          <Bucket label="Equipment" total={(t.equipment_per_kg ?? 0) * (t.kg_sold ?? 0)} perKg={t.equipment_per_kg} kgSold={t.kg_sold}
                  hint={`amort R${fmt(t.annual_equipment_amortisation, 0)}/yr ÷ ${fmt(t.kg_sold_prior_fy, 0)}kg prior FY`} />
          <Bucket label="Packaging" total={t.packaging_total} perKg={t.packaging_per_kg} kgSold={t.kg_sold} />
        </div>
      )}

      {/* Pricebook selector */}
      {pricebooks.length > 0 && (
        <div className="bg-card border border-border rounded p-3 mb-4 flex flex-wrap items-center gap-2 no-print" data-testid="pricebook-selector">
          <Tag className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="label-tiny">Sell price from:</span>
          {pricebooks.map((pb) => (
            <button key={pb.id} onClick={() => setSelectedPbId(pb.id)}
              data-testid={`pricebook-${pb.id}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                selectedPbId === pb.id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border hover:bg-secondary"
              }`}>
              {pb.display_name || pb.name}
              {pb.is_default && <span className="ml-1 opacity-60">(default)</span>}
            </button>
          ))}
          <Link to="/settings"
            data-testid="pricebook-settings-link"
            className="text-[10px] text-muted-foreground underline ml-1 inline-flex items-center gap-0.5 hover:text-foreground">
            <ExternalLink className="w-2.5 h-2.5" /> manage in Integrations
          </Link>
          <button onClick={refreshPricebooks} disabled={pbBusy}
            data-testid="pricebook-refresh"
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] border border-border rounded hover:bg-secondary disabled:opacity-60">
            <RefreshCw className={`w-3 h-3 ${pbBusy ? "animate-spin" : ""}`} /> Refresh from Zoho
          </button>
          {pbSyncedAt && (
            <span className="text-[10px] text-muted-foreground font-mono">
              synced {new Date(pbSyncedAt).toLocaleString("en-ZA")}
            </span>
          )}
        </div>
      )}

      {/* Per-item table with DnD */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedRows.map((r) => r.item_id)} strategy={verticalListSortingStrategy}>
          <div className="bg-card border border-border rounded overflow-x-auto">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="mfg-items-table">
                <thead className="bg-secondary">
                  <tr>
                    <th className="w-8 p-2"></th>
                    <th className="text-left p-3 label-tiny">Item</th>
                    <th className="text-left p-3 label-tiny">Blend</th>
                    <th className="text-right p-3 label-tiny">Units</th>
                    <th className="text-right p-3 label-tiny">Loss %</th>
                    <th className="text-right p-3 label-tiny">Roasted</th>
                    <th className="text-right p-3 label-tiny">Gas</th>
                    <th className="text-right p-3 label-tiny">Freight</th>
                    <th className="text-right p-3 label-tiny">Labour</th>
                    <th className="text-right p-3 label-tiny">Eqt</th>
                    <th className="text-right p-3 label-tiny">Pkg</th>
                    <th className="text-right p-3 label-tiny font-bold">Total / unit</th>
                    <th className="text-right p-3 label-tiny">Sell / unit</th>
                    <th className="text-right p-3 label-tiny font-bold">GM%</th>
                  </tr>
                </thead>
                <tbody>
                  {!data && <tr><td colSpan="14" className="p-8 text-center text-muted-foreground">{busy ? "Loading…" : "—"}</td></tr>}
                  {data && orderedRows.length === 0 && (
                    <tr><td colSpan="14" className="p-8 text-center text-muted-foreground text-xs">
                      No items included in Manufacturing Cost yet. Open <Link to="/item-mapping" className="text-primary underline">Item Mapping</Link> and tick the <strong>Mfg Cost</strong> checkbox on the items you want to track.
                    </td></tr>
                  )}
                  {orderedRows.map((r) => (
                    <SortableRow key={r.item_id} row={r} pbItem={pbItemPrices[r.item_id]} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SortableContext>
      </DndContext>

      {/* Print-only footer */}
      <div className="print-only" style={{ marginTop: "8mm", borderTop: "1px solid #d4d4d0", paddingTop: "4mm", fontSize: "7.5pt", color: "#888", textAlign: "center" }}>
        <p style={{ margin: 0 }}>
          Brothers Coffee Roastery — Manufacturing Cost — Costs in ZAR · Per unit basis · Sell price source: {pricebooks.find((p) => p.id === selectedPbId)?.display_name || pricebooks.find((p) => p.id === selectedPbId)?.name || "item default rates"}
        </p>
      </div>
    </div>
  );
}

function SortableRow({ row, pbItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.item_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "hsl(40 21% 92%)" : undefined,
  };

  // Sell price per UNIT — prefer pricebook (rate is per unit), fall back to item.rate
  const sellPerUnit = pbItem ? pbItem.rate : row.rate_per_unit;
  const totalCostPerUnit = row.total_cost_per_unit;
  const gmPct = (sellPerUnit != null && sellPerUnit > 0)
    ? ((sellPerUnit - totalCostPerUnit) / sellPerUnit) * 100.0
    : null;
  const gmTone = (gmPct == null) ? "text-muted-foreground" :
                  gmPct >= 50 ? "text-[hsl(132,18%,36%)]" :
                  gmPct >= 30 ? "text-foreground" : "text-destructive";
  const sellSource = pbItem?.source === "pricebook" ? "" :
                      pbItem?.source === "item_default" ? "default" :
                      pbItem ? "missing" : "";

  return (
    <tr ref={setNodeRef} style={style}
        className="border-t border-border hover:bg-secondary/30"
        data-testid={`mfg-row-${row.item_id}`}>
      <td className="p-2 align-middle text-center">
        <button {...attributes} {...listeners}
          data-testid={`mfg-handle-${row.item_id}`}
          className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing inline-block p-1"
          title="Drag to reorder">
          <GripVertical className="w-4 h-4" />
        </button>
      </td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium truncate" title={row.name}>{row.name}</p>
            {row.sku && <p className="text-[10px] text-muted-foreground font-mono">{row.sku} · {fmt(row.kg_per_unit, 3)} kg/unit</p>}
          </div>
        </div>
      </td>
      <td className="p-3 text-xs text-muted-foreground truncate">{row.blend_name}</td>
      <td className="p-3 text-right font-mono">{fmt(row.qty, 0)}</td>
      <td className="p-3 text-right font-mono text-xs text-muted-foreground">{fmt(row.roast_loss_pct, 1)}%</td>
      <td className="p-3 text-right font-mono">R {fmt(row.roasted_cost_per_unit)}</td>
      <td className="p-3 text-right font-mono text-xs">R {fmt(row.gas_per_unit)}</td>
      <td className="p-3 text-right font-mono text-xs">R {fmt(row.freight_per_unit)}</td>
      <td className="p-3 text-right font-mono text-xs">R {fmt(row.labour_per_unit)}</td>
      <td className="p-3 text-right font-mono text-xs">R {fmt(row.equipment_per_unit)}</td>
      <td className="p-3 text-right font-mono text-xs">R {fmt(row.packaging_per_unit)}</td>
      <td className="p-3 text-right font-mono font-bold text-primary">R {fmt(totalCostPerUnit)}</td>
      <td className="p-3 text-right font-mono" data-testid={`mfg-sell-${row.item_id}`}>
        {sellPerUnit > 0 ? (
          <>
            <p>R {fmt(sellPerUnit)}</p>
            {sellSource && <p className="text-[9px] text-muted-foreground italic">{sellSource}</p>}
          </>
        ) : <span className="text-muted-foreground text-xs">—</span>}
      </td>
      <td className={`p-3 text-right font-mono font-bold ${gmTone}`} data-testid={`mfg-gm-${row.item_id}`}>
        {gmPct != null ? `${fmt(gmPct, 1)}%` : "—"}
      </td>
    </tr>
  );
}

function Bucket({ label, total, perKg, kgSold, hint }) {
  const noKg = !kgSold || kgSold <= 0;
  const tone = (total ?? 0) > 0 ? "border-primary/30 bg-primary/5" : "border-border bg-card";
  return (
    <div className={`rounded p-3 border ${tone}`} data-testid={`bucket-${label.toLowerCase()}`}>
      <p className="label-tiny mb-1">{label}</p>
      <p className="font-display text-lg font-bold tabular-nums">
        {(perKg != null && perKg > 0) ? `R ${fmt(perKg, 2)}/kg` : "—"}
      </p>
      <p className="text-[10px] text-muted-foreground font-mono">
        {(total ?? 0) > 0 ? `R ${fmt(total, 0)} total` : "no cost"}
        {noKg && (total ?? 0) > 0 && <span className="text-amber-700"> · no kg sold yet</span>}
      </p>
      {hint && <p className="text-[9px] text-muted-foreground italic mt-1 truncate" title={hint}>{hint}</p>}
    </div>
  );
}
